import { useMemo } from 'react';

import { useGetTasksQuery } from '@/services/api/tasksService';
import { MatterStatus, Task } from '@/services/types/client-intake-types';
import {
  CanonicalTaskGroup,
  CanonicalTaskStatus,
} from '@/services/types/task-types';

import { MatterSectionCard } from './MatterSectionCard';

interface StatusSummaryCardProps {
  matterId: string;
  matterStatus?: string;
}

// Define the main workflow phases for the progress bar
const WORKFLOW_PHASES = [
  {
    key: 'initial_review',
    label: 'Initial Review',
    status: MatterStatus.INITIAL_REVIEW,
  },
  { key: 'case_setup', label: 'Case Setup', status: MatterStatus.CASE_SETUP },
  { key: 'treatment', label: 'Treatment', status: MatterStatus.TREATMENT },
  { key: 'demand', label: 'Demand', status: MatterStatus.DEMAND_PREPARATION },
  { key: 'complete', label: 'Complete', status: MatterStatus.COMPLETE },
] as const;

// Map task groups to summary categories
const CATEGORY_CONFIG: Record<
  string,
  { label: string; groups: CanonicalTaskGroup[] }
> = {
  liability: {
    label: 'Liability',
    groups: [
      CanonicalTaskGroup.POLICE_REPORT_INVESTIGATION,
      CanonicalTaskGroup.INTAKE,
      CanonicalTaskGroup.INITIAL_REVIEW,
    ],
  },
  claims: {
    label: 'Claims',
    groups: [
      CanonicalTaskGroup.FIRST_PARTY_INSURANCE,
      CanonicalTaskGroup.THIRD_PARTY_INSURANCE,
      CanonicalTaskGroup.ADDITIONAL_INSURANCE,
    ],
  },
  treatment: {
    label: 'Treatment',
    groups: [CanonicalTaskGroup.TREATMENT, CanonicalTaskGroup.MEDICAL_RECORDS],
  },
};

type CategoryKey = keyof typeof CATEGORY_CONFIG;

interface CategorySummary {
  label: string;
  completed: number;
  total: number;
  description: string;
}

function getCategoryStatusColor(total: number, completed: number): string {
  if (total === 0) return 'text-zinc-400';
  if (completed === total) return 'text-green-600';
  return 'text-primary';
}

function getCategoryStatusText(total: number, completed: number): string {
  if (total === 0) return 'No tasks';
  if (completed === total) return 'All tasks complete';
  return `${completed}/${total} tasks completed`;
}

function getPhaseIndex(status: string | undefined): number {
  if (!status) return 0;

  // Map various statuses to phase indices
  const statusLower = status.toLowerCase();

  if (statusLower.includes('new') || statusLower.includes('lead')) return 0;
  if (statusLower.includes('initial') || statusLower.includes('intake'))
    return 0;
  if (statusLower.includes('case setup')) return 1;
  if (statusLower.includes('treatment')) return 2;
  if (statusLower.includes('demand') || statusLower.includes('ready')) return 3;
  if (statusLower.includes('complete')) return 4;

  return 0;
}

function generateCategoryDescription(
  categoryKey: CategoryKey,
  tasks: Task[],
  completed: number,
  total: number,
): string {
  if (total === 0) return 'No tasks in this category.';

  if (completed === total) {
    switch (categoryKey) {
      case 'liability':
        return 'Complete. Police report, witness statements, and collision diagram received and reviewed.';
      case 'claims':
        return 'All claims filed and processed.';
      case 'treatment':
        return 'Treatment complete. All records received.';
      default:
        return 'All tasks complete.';
    }
  }

  // Get some context about in-progress tasks
  const inProgressTasks = tasks.filter(
    (t) => t.status.canonical_status === CanonicalTaskStatus.IN_PROGRESS,
  );
  const pendingTasks = tasks.filter(
    (t) => t.status.canonical_status === CanonicalTaskStatus.PENDING,
  );

  switch (categoryKey) {
    case 'liability':
      if (inProgressTasks.length > 0) {
        return `${inProgressTasks[0].name} in progress.`;
      }
      return `${pendingTasks.length} task${pendingTasks.length !== 1 ? 's' : ''} pending.`;
    case 'claims':
      if (inProgressTasks.length > 0) {
        return `${inProgressTasks[0].name} in progress.`;
      }
      return `Initial claim filed. ${pendingTasks.length} task${pendingTasks.length !== 1 ? 's' : ''} remaining.`;
    case 'treatment':
      if (inProgressTasks.length > 0) {
        return `Active treatment ongoing. ${inProgressTasks[0].name} in progress.`;
      }
      return `${completed} of ${total} treatment tasks completed.`;
    default:
      return `${completed} of ${total} tasks completed.`;
  }
}

export const StatusSummaryCard = ({
  matterId,
  matterStatus,
}: StatusSummaryCardProps) => {
  // Fetch all tasks for the matter
  const { data: tasksData, isLoading } = useGetTasksQuery({
    matter: matterId,
    limit: 200,
  });

  // Calculate current phase progress
  // Progress fills to the center of each label slot, except complete fills to 100%
  const currentPhaseIndex = getPhaseIndex(matterStatus);
  const progressPercent =
    currentPhaseIndex === WORKFLOW_PHASES.length - 1
      ? 100
      : ((currentPhaseIndex + 0.5) / WORKFLOW_PHASES.length) * 100;

  // Calculate category summaries
  const categorySummaries = useMemo<
    Record<CategoryKey, CategorySummary>
  >(() => {
    const tasksList = tasksData?.results || [];

    return Object.entries(CATEGORY_CONFIG).reduce(
      (summaries, [key, config]) => {
        const categoryKey = key as CategoryKey;
        const categoryTasks = tasksList.filter((task) =>
          config.groups.includes(
            task.group.canonical_group as CanonicalTaskGroup,
          ),
        );

        const completed = categoryTasks.filter(
          (t) =>
            t.status.canonical_status === CanonicalTaskStatus.COMPLETED ||
            t.status.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE ||
            t.status.canonical_status === CanonicalTaskStatus.CLOSED,
        ).length;

        const total = categoryTasks.length;

        return {
          ...summaries,
          [categoryKey]: {
            label: config.label,
            completed,
            total,
            description: generateCategoryDescription(
              categoryKey,
              categoryTasks,
              completed,
              total,
            ),
          },
        };
      },
      {} as Record<CategoryKey, CategorySummary>,
    );
  }, [tasksData?.results]);

  return (
    <MatterSectionCard title="Status Summary" isLoading={isLoading}>
      {/* Progress Bar */}
      <div className="mb-2">
        <div className="h-2 w-full bg-zinc-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Phase Labels */}
      <div className="grid grid-cols-5 mb-8">
        {WORKFLOW_PHASES.map((phase, index) => (
          <span
            key={phase.key}
            className={`text-xs font-medium text-center ${
              index <= currentPhaseIndex ? 'text-primary' : 'text-zinc-400'
            }`}
          >
            {phase.label}
          </span>
        ))}
      </div>

      {/* Category Summaries */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {(
          Object.entries(categorySummaries) as [CategoryKey, CategorySummary][]
        ).map(([key, summary]) => (
          <div key={key}>
            <h3 className="text-sm font-semibold text-zinc-900 mb-1">
              {summary.label}
            </h3>
            <p
              className={`text-sm font-medium mb-2 ${getCategoryStatusColor(summary.total, summary.completed)}`}
            >
              {getCategoryStatusText(summary.total, summary.completed)}
            </p>
            <p className="text-xs text-zinc-700 leading-tight font-medium">
              {summary.description}
            </p>
          </div>
        ))}
      </div>
    </MatterSectionCard>
  );
};
